/**
 * Jovii — one person, publicly sourced: the data behind `/person/:key`.
 *
 * The owner's ask: click a cast face or a director's name and get "their
 * profile … their picture and some information about them — if it's not already
 * on the wiki, we need to find where it is publicly available." Everything here
 * is browser-direct against the same public, CORS-open hosts the cast lane
 * already uses, plus two more, both probed live 2026-08-17:
 *
 *  - Wikidata (CC0 data): the person entity — label, description, P18 image,
 *    P569/P570 life dates, P106 occupations, and the enwiki sitelink.
 *  - Wikipedia REST summaries (`/api/rest_v1/page/summary/…`, `ACAO: *`,
 *    CC BY-SA text): the short bio paragraph. Attribution is a visible "From
 *    Wikipedia" link on the page — CC BY-SA text, unlike CC0 data, needs it.
 *  - Wikidata Query Service (query.wikidata.org, `ACAO: *`): ONE SPARQL query
 *    for the filmography — every work that names this person as cast member
 *    (P161) or director (P57) and carries an IMDb id (P345), ranked by
 *    sitelink count (the fame proxy that put Breaking Bad and The Dark Knight
 *    first in the probes) and classified film/series via P31/P279*, because
 *    Cinemeta's movie endpoint FABRICATES records for series ids (SESSION-
 *    HANDOFF sharp edge 10) — an id of unknown kind is dropped, never guessed.
 *    The answer is split three ways for the page's three rails — Movies
 *    (acted, film), Series (acted, series), Directed (P57, both kinds) — and a
 *    title someone both acted in and directed honestly appears in both its
 *    acted rail and Directed. Fame order holds within each rail.
 *  - TVMaze people search for the page portrait, under cast.ts's strict
 *    birthday-or-unique gate (docs/CAST.md § K); Commons P18 at the 500px
 *    bucket is the fallback.
 *
 * The KEY is a Wikidata QID when the caller has one (cast faces do — the work
 * chain resolves people by id) and a plain name otherwise (director credits,
 * TVMaze-sourced rows). A name key re-resolves here through wbsearchentities
 * filtered by screen occupations — the cast lane's measured defence against
 * wrong-person hits, widened to directors. A person nothing resolves still
 * renders: name, their titles absent, a sentence saying so. Never a broken page.
 *
 * Fetch etiquette: the whole page is ≤5 JSON requests before the poster grid,
 * every one fails soft, and answers are cached in memory and sessionStorage
 * (`jt.person.v1` — session-scoped: people pages are visited in bursts, and a
 * day-old bio is not worth a schema in localStorage).
 *
 * Owned by the CAST lane. Imported by routes/Person.svelte only.
 */

import type { MediaKind } from '$types';
import {
  bestImageFile,
  claimDate,
  claimItemId,
  commonsFilePage,
  commonsThumb,
  pickActor,
  tvmazePersonPortrait,
  type WdEntity
} from './cast';

/* ────────────────────────────────────────────────────────────────────────────
 * Shapes
 * ──────────────────────────────────────────────────────────────────────────── */

export interface PersonProfile {
  /** The route key this profile answers for. */
  key: string;
  qid?: string;
  name: string;
  /** Wikidata's one-liner, e.g. "American actor (born 1956)". */
  description?: string;
  born?: number;
  died?: number;
  /** From P106 — these decide which filmography section leads. */
  actor: boolean;
  director: boolean;
  /** The page portrait (large), and where its credit lives. */
  photo?: string;
  photoCredit?: { label: string; url: string };
  /** The short bio and its CC BY-SA attribution target. */
  bio?: string;
  wikiUrl?: string;
}

/** One work off the SPARQL answer, not yet resolved against the catalogue. */
export interface PersonCredit {
  imdbId: string;
  kind: MediaKind;
  role: 'cast' | 'director';
  /** Wikidata sitelink count — the fame proxy the sections are ordered by. */
  fame: number;
  year?: number;
}

/**
 * The filmography, already split for the page's three rails. Split HERE and
 * not in the view because the rule has teeth: an acted rail is single-kind by
 * construction (its Posters drop the kind tag), Directed pools kinds (its
 * Posters keep it), and a credit landing in the wrong list would quietly
 * break that contract. Each list keeps the fame order the query answered in.
 */
export interface PersonFilmography {
  /** Acted in, kind movie (P161 ∩ film). */
  movies: PersonCredit[];
  /** Acted in, kind series (P161 ∩ series). */
  series: PersonCredit[];
  /** Directed (P57), both kinds pooled — the rail's tiles say which is which. */
  directed: PersonCredit[];
}

export interface PersonPage {
  profile: PersonProfile;
  filmography: PersonFilmography;
}

/* ────────────────────────────────────────────────────────────────────────────
 * Constants
 * ──────────────────────────────────────────────────────────────────────────── */

const WD_API = 'https://www.wikidata.org/w/api.php';
const SPARQL = 'https://query.wikidata.org/sparql';
const WIKI_SUMMARY = 'https://en.wikipedia.org/api/rest_v1/page/summary';

/** The cast lane's measured actor set (docs/CAST.md § D)… */
const ACTORISH = new Set(['Q33999', 'Q10800557', 'Q10798782', 'Q2405480', 'Q2259451']);
/** …plus the director occupations, labels verified live 2026-08-17:
 *  Q2526255 "film director", Q3455803 "director", Q2059704 "television director". */
const DIRECTORISH = new Set(['Q2526255', 'Q3455803', 'Q2059704']);

const CACHE_TTL_MS = 24 * 60 * 60 * 1000;
const MAX_CACHED_PEOPLE = 20;
/** v2: the page went from a flat `credits` list to the three-rail
 *  `filmography` split — a v1 entry must never masquerade as a v2 answer. */
const SESSION_KEY = 'jt.person.v2';

/* ────────────────────────────────────────────────────────────────────────────
 * Pure pieces, exported for the unit suite
 * ──────────────────────────────────────────────────────────────────────────── */

/** A person key is a QID (`/person/Q23547`) or a display name (`/person/Bong%20Joon-ho`). */
export function isQid(key: string): boolean {
  return /^Q\d+$/.test(key);
}

/** The year alone, at any precision — for the "1956–2014" line, where a
 *  year-precision date is still worth showing (unlike the birthday matcher). */
export function claimYear(claims: WdEntity['claims'], property: string): number | undefined {
  const value = claims?.[property]?.[0]?.mainsnak?.datavalue?.value;
  if (typeof value !== 'object' || typeof value?.time !== 'string') return undefined;
  const m = /^\+(\d{4})/.exec(value.time);
  return m ? Number(m[1]) : undefined;
}

/**
 * Wikidata descriptions routinely end in a parenthetical the header already
 * states — "American actor (born 1956)" beside our own "1956" read twice in
 * verification. Stripped only when we HAVE the year; otherwise the
 * parenthetical is the only date on the page and stays.
 */
export function tidyDescription(description: string, hasYears: boolean): string {
  if (!hasYears) return description;
  return description.replace(/\s*\((?:born|b\.)[^)]*\)$/i, '').replace(/\s*\(\d{4}[–-]\d{4}\)$/, '');
}

/** P106 → which hats this person wears, for section order and emphasis. */
export function screenRoles(claims: WdEntity['claims']): { actor: boolean; director: boolean } {
  const occupations = (claims?.['P106'] ?? []).map((c) => claimItemId(c) ?? '');
  return {
    actor: occupations.some((q) => ACTORISH.has(q)),
    director: occupations.some((q) => DIRECTORISH.has(q))
  };
}

/**
 * The filmography query. One request for both roles; every clause probed live
 * (probe log 2026-08-17): P31/P279* correctly separated Breaking Bad
 * (series 1 / film 0) from Saving Private Ryan (0 / 1); sitelink fame put the
 * famous work first; P577∥P580 covers films and series' start dates.
 *
 * LIMIT 500 is "the whole career", not a window: the rails scroll until
 * Wikidata's list is exhausted, so the list has to BE the list. One row per
 * work-and-role, and even the longest screen careers ran a few hundred rows
 * in the probes — 500 is a seatbelt against a data-error blowup, still one
 * paced request, never paged.
 */
export function filmographyQuery(qid: string): string {
  return `SELECT ?imdb ?role (MAX(?links) AS ?fame) (MIN(YEAR(?when)) AS ?year)
 (MAX(?isSeries) AS ?series) (MAX(?isFilm) AS ?film) WHERE {
 VALUES ?role { wdt:P161 wdt:P57 }
 ?work ?role wd:${qid} ; wdt:P345 ?imdb ; wikibase:sitelinks ?links .
 FILTER(STRSTARTS(?imdb, "tt"))
 OPTIONAL { ?work wdt:P577 ?d1 } OPTIONAL { ?work wdt:P580 ?d2 }
 BIND(COALESCE(?d1, ?d2) AS ?when)
 BIND(IF(EXISTS { ?work wdt:P31/wdt:P279* wd:Q5398426 }, 1, 0) AS ?isSeries)
 BIND(IF(EXISTS { ?work wdt:P31/wdt:P279* wd:Q11424 }, 1, 0) AS ?isFilm)
} GROUP BY ?imdb ?role ORDER BY DESC(?fame) LIMIT 500`;
}

/** One SPARQL binding row, loosely typed — nothing off the wire is trusted. */
export interface SparqlBinding {
  imdb?: { value?: string };
  role?: { value?: string };
  fame?: { value?: string };
  year?: { value?: string };
  series?: { value?: string };
  film?: { value?: string };
}

/**
 * Bindings → credits. The kind rule is the sharp-edge-10 rule: a work whose
 * P31 closure reaches "film" is a movie, else "television series" makes it a
 * series, else it is DROPPED — asking Cinemeta's movie endpoint about an
 * unknown id is how a 1982 erotic film ends up on House of the Dragon's page.
 */
export function mapFilmography(raw: unknown): PersonCredit[] {
  const bindings = (raw as { results?: { bindings?: SparqlBinding[] } })?.results?.bindings;
  if (!Array.isArray(bindings)) return [];
  const credits: PersonCredit[] = [];
  const seen = new Set<string>();
  for (const b of bindings) {
    const imdbId = b?.imdb?.value;
    const roleUri = b?.role?.value ?? '';
    if (!imdbId || !/^tt\d{5,10}$/.test(imdbId)) continue;
    const role = roleUri.endsWith('P57') ? 'director' : roleUri.endsWith('P161') ? 'cast' : null;
    if (!role || seen.has(`${role}:${imdbId}`)) continue;
    const film = b?.film?.value === '1';
    const series = b?.series?.value === '1';
    const kind: MediaKind | null = film ? 'movie' : series ? 'series' : null;
    if (!kind) continue;
    seen.add(`${role}:${imdbId}`);
    const credit: PersonCredit = { imdbId, kind, role, fame: Number(b?.fame?.value ?? 0) || 0 };
    const year = Number(b?.year?.value);
    if (Number.isInteger(year) && year > 1880) credit.year = year;
    credits.push(credit);
  }
  // The service already orders by fame, but nothing off the wire is trusted.
  credits.sort((a, b) => b.fame - a.fame);
  return credits;
}

/**
 * Credits → the three rails. A stable partition of a fame-ordered list, so
 * fame order survives into each rail for free. A title both acted-in and
 * directed arrives as TWO credits (one per role) and lands in both its acted
 * rail and Directed — that's honest, not a bug. An empty category is an empty
 * array, and the page draws NOTHING for it: no heading, no ghost row.
 */
export function splitFilmography(credits: PersonCredit[]): PersonFilmography {
  const split: PersonFilmography = { movies: [], series: [], directed: [] };
  for (const credit of credits) {
    if (credit.role === 'director') split.directed.push(credit);
    else if (credit.kind === 'movie') split.movies.push(credit);
    else split.series.push(credit);
  }
  return split;
}

/** Cache entries, pruned exactly the way the cast cache is. */
export function prunePersonCache(
  cache: Record<string, { at: number; page: PersonPage }>,
  now: number,
  max = MAX_CACHED_PEOPLE,
  ttl = CACHE_TTL_MS
): Record<string, { at: number; page: PersonPage }> {
  const alive = Object.entries(cache).filter(([, v]) => v && now - v.at < ttl && !!v.page?.profile);
  alive.sort((a, b) => b[1].at - a[1].at);
  return Object.fromEntries(alive.slice(0, max));
}

/* ────────────────────────────────────────────────────────────────────────────
 * Wire
 * ──────────────────────────────────────────────────────────────────────────── */

async function getJson<T>(url: string | URL, signal?: AbortSignal): Promise<T | null> {
  const response = await fetch(url, {
    signal: signal ?? null,
    credentials: 'omit',
    headers: { accept: 'application/json' }
  });
  if (!response.ok) return null;
  return (await response.json()) as T;
}

function wdUrl(params: Record<string, string>): URL {
  const url = new URL(WD_API);
  url.searchParams.set('format', 'json');
  url.searchParams.set('origin', '*');
  for (const [k, v] of Object.entries(params)) url.searchParams.set(k, v);
  return url;
}

const ENTITY_PROPS = 'labels|descriptions|claims|sitelinks';

/** A name → the person's entity, through the occupation gate. Null = nobody
 *  defensible; the page says so rather than guessing (docs/CAST.md § D). */
async function resolveName(name: string, signal?: AbortSignal): Promise<{ qid: string; entity: WdEntity } | null> {
  const found = await getJson<{ search?: { id?: string }[] }>(
    wdUrl({ action: 'wbsearchentities', search: name, language: 'en', type: 'item', limit: '5' }),
    signal
  );
  const ids = (found?.search ?? []).map((s) => s.id).filter((id): id is string => typeof id === 'string');
  if (!ids.length) return null;
  const batch = await getJson<{ entities?: Record<string, WdEntity> }>(
    wdUrl({ action: 'wbgetentities', ids: ids.join('|'), props: ENTITY_PROPS, languages: 'en', sitefilter: 'enwiki' }),
    signal
  );
  const entities = batch?.entities ?? {};
  // pickActor is the cast lane's measured filter; widen it with the director set.
  const hit =
    pickActor(ids, entities) ??
    ids.find((id) =>
      ((entities[id]?.claims?.['P106'] ?? []).map((c) => claimItemId(c) ?? '')).some((q) => DIRECTORISH.has(q))
    ) ??
    null;
  const entity = hit ? entities[hit] : undefined;
  return hit && entity ? { qid: hit, entity } : null;
}

/** The Wikipedia summary — `type: 'standard'` only, so a disambiguation page
 *  can never masquerade as a biography. */
async function wikiBio(
  title: string,
  signal?: AbortSignal
): Promise<{ bio: string; url: string } | null> {
  const slug = encodeURIComponent(title.replace(/ /g, '_'));
  const summary = await getJson<{ type?: string; extract?: string }>(`${WIKI_SUMMARY}/${slug}`, signal);
  if (summary?.type !== 'standard' || !summary.extract) return null;
  return { bio: summary.extract, url: `https://en.wikipedia.org/wiki/${slug}` };
}

/* ────────────────────────────────────────────────────────────────────────────
 * The cache, and the one public entry point
 * ──────────────────────────────────────────────────────────────────────────── */

// Old session entries lack completeness evidence and are refreshed once, not deleted.
type CachedPerson = { at: number; page: PersonPage; complete: true };
let memory: Record<string, CachedPerson> = Object.create(null);

function readSession(key: string): CachedPerson | null {
  try {
    const file = JSON.parse(sessionStorage.getItem(SESSION_KEY) ?? '{}') as Record<
      string,
      CachedPerson
    >;
    const hit = file[key];
    return hit && hit.complete === true && Date.now() - hit.at < CACHE_TTL_MS && hit.page?.profile && hit.page?.filmography
      ? hit
      : null;
  } catch {
    return null; // Safari private mode, or a corrupt entry  // IOS-UNVERIFIED
  }
}

function persistSession(key: string, page: PersonPage): void {
  try {
    const file = JSON.parse(sessionStorage.getItem(SESSION_KEY) ?? '{}') as Record<
      string,
      CachedPerson
    >;
    Object.defineProperty(file, key, { value: { at: Date.now(), page, complete: true }, enumerable: true, configurable: true, writable: true });
    sessionStorage.setItem(SESSION_KEY, JSON.stringify(prunePersonCache(file, Date.now())));
  } catch {
    /* a cache that cannot write is still a page that renders */
  }
}

/**
 * Everything `/person/:key` needs, in ≤5 JSON requests, each failing soft:
 * resolve → entity → (portrait + bio in parallel) → filmography. The returned
 * profile always has at least a name; `credits` may be empty and the page says
 * why gracefully.
 */
export async function loadPerson(key: string, signal?: AbortSignal): Promise<PersonPage> {
  signal?.throwIfAborted();
  memory = Object.assign(Object.create(null), prunePersonCache(memory, Date.now()));
  const cached = memory[key] ?? readSession(key);
  if (cached) {
    memory[key] = cached;
    memory = Object.assign(Object.create(null), prunePersonCache(memory, Date.now()));
    return cached.page;
  }

  const swallow = (err: unknown): null => {
    if ((err as Error)?.name === 'AbortError') throw err;
    return null;
  };

  let qid: string | null = null;
  let entity: WdEntity | undefined;

  if (isQid(key)) {
    qid = key;
    const batch = await getJson<{ entities?: Record<string, WdEntity> }>(
      wdUrl({ action: 'wbgetentities', ids: key, props: ENTITY_PROPS, languages: 'en', sitefilter: 'enwiki' }),
      signal
    ).catch(swallow);
    entity = batch?.entities?.[key];
  } else {
    const resolved = await resolveName(key, signal).catch(swallow);
    if (resolved) {
      qid = resolved.qid;
      entity = resolved.entity;
    }
  }

  const name = entity?.labels?.['en']?.value ?? (isQid(key) ? 'Unknown person' : key);
  const roles = screenRoles(entity?.claims);
  const profile: PersonProfile = { key, name, actor: roles.actor, director: roles.director };
  if (qid && entity) profile.qid = qid;
  const born = claimYear(entity?.claims, 'P569');
  if (born) profile.born = born;
  const died = claimYear(entity?.claims, 'P570');
  if (died) profile.died = died;
  const description = entity?.descriptions?.['en']?.value;
  if (description) {
    const tidy = tidyDescription(description, !!born);
    if (tidy) profile.description = tidy;
  }

  // Portrait, bio and filmography are independent — ask together.
  const bornFull = claimDate(entity?.claims?.['P569']?.[0]) ?? undefined;
  const enwiki = entity?.sitelinks?.['enwiki']?.title;
  const [portrait, bio, sparql] = await Promise.all([
    entity ? tvmazePersonPortrait(name, bornFull, signal).catch(swallow) : Promise.resolve(null),
    enwiki ? wikiBio(enwiki, signal).catch(swallow) : Promise.resolve(null),
    qid && entity
      ? getJson<unknown>(`${SPARQL}?query=${encodeURIComponent(filmographyQuery(qid))}&format=json`, signal).catch(
          swallow
        )
      : Promise.resolve(null)
  ]);

  if (portrait) {
    profile.photo = portrait.large ?? portrait.photo;
    profile.photoCredit = { label: 'Photo: TVMaze', url: portrait.creditUrl };
  } else {
    const file = bestImageFile(entity?.claims?.['P18']);
    if (file) {
      profile.photo = commonsThumb(file, 500);
      profile.photoCredit = { label: 'Photo: Wikimedia Commons', url: commonsFilePage(file) };
    }
  }
  if (bio) {
    profile.bio = bio.bio;
    profile.wikiUrl = bio.url;
  }

  const page: PersonPage = { profile, filmography: splitFilmography(mapFilmography(sparql)) };
  // Only a page that actually resolved is worth remembering; a network blip
  // should be re-asked on the next visit, not cached for a day.
  signal?.throwIfAborted();
  if (entity && Array.isArray((sparql as { results?: { bindings?: unknown } })?.results?.bindings)) {
    memory[key] = { at: Date.now(), page, complete: true };
    memory = Object.assign(Object.create(null), prunePersonCache(memory, Date.now()));
    persistSession(key, page);
  }
  return page;
}
